import React, { useCallback, useEffect, useState } from 'react';
import { placeholderStats } from '../../utils/fallbackData';
import { fetchAdminStats } from '../../api/adminClient';
import { formatCurrency } from '../../utils/formatters';
import UserManagement from '../management/UserManagement';
import OrganizationManagement from '../management/OrganizationManagement';
import SubscriptionsAndTransactions from '../management/SubscriptionsAndTransactions';

const DASHBOARD_TABS = [
  { key: 'users', label: 'User Management' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'subscriptions', label: 'Subscriptions & Transactions' },
];

const Dashboard = ({
  users,
  usersLoading,
  usersPagination,
  usersStatusFilter,
  usersSort,
  onUsersPageChange,
  onUsersStatusChange,
  onUsersEmailSearch,
  onUsersDateSearch,
  onUsersSortChange,
  onRefreshUsers,
}) => {
  const [dashboardStats, setDashboardStats] = useState(placeholderStats)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('users');

  const load = useCallback(async () => {
      setLoading(true);
      try {
        const res = await fetchAdminStats();
        if (res.data?.success) {
                  const s = res.data.stats;
                  setDashboardStats([
                    {
                      title: 'Total Users',
                      value: s.total_users,
                      change: '—',
                      accent: 'primary',
                      progress: Math.min(s.total_users * 2, 100),
                    },
                    {
                      title: 'Total Organizations',
                      value: s.total_organizations,
                      change: '—',
                      accent: 'primary',
                      progress: Math.min(s.total_organizations * 10, 100),
                    },
                    {
                      title: 'Active Subscriptions',
                      value: s.active_subscriptions,
                      change: s.active_subscriptions > 0 ? '+growth' : '—',
                      accent: 'success',
                      progress: Math.min(s.active_subscriptions * 8, 100),
                    },
                    {
                      title: 'Trialing Subscriptions',
                      value: s.trialing_subscriptions,
                      change: s.trialing_subscriptions > 0 ? 'in trial' : '—',
                      accent: 'info',
                      progress: Math.min(s.trialing_subscriptions * 10, 100),
                    },
                    {
                      title: 'Pending Subscriptions',
                      value: s.pending_subscriptions,
                      change: s.pending_subscriptions > 0 ? 'needs review' : '—',
                      accent: 'warning',
                      progress: Math.min(s.pending_subscriptions * 10, 100),
                    },
                    {
                      title: 'Freemium Users',
                      value: s.freemium_users,
                      change: s.freemium_users > 0 ? 'on free minutes' : '—',
                      accent: 'info',
                      progress: Math.min(s.freemium_users * 10, 100),
                    },
                    {
                      title: 'Monthly Revenue',
                      value: formatCurrency(s.monthly_revenue),
                      change: s.monthly_revenue > 0 ? '+recent' : '—',
                      accent: 'primary',
                      progress: Math.min(Math.floor(s.monthly_revenue / 50), 100),
                    },
                  ]);
                  
                }
      } catch {
        setDashboardStats(placeholderStats);
      } finally {
        setLoading(false);
      }
    }, []);
  
    useEffect(() => { load(); }, [load]);
  
  return(
  <div className="dashboard-view">
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
      <div>
        <h1 className="display-5 fw-semibold mb-1">Admin Overview</h1>
        <p className="text-muted mb-0">
          Track SLP verifications, subscriptions, and policy updates in real-time.
        </p>
      </div>
    </div>

    <div className="stats-grid">
      {dashboardStats && dashboardStats?.map((stat) => (
        <div key={stat.title}>
          <div className="card-soft h-100 d-flex flex-column justify-content-between">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <span className="badge shadow-sm rounded-pill bg-white text-uppercase text-muted small fw-semibold">
                  {stat.title}
                </span>
                <h2 className="display-6 fw-semibold mt-3">{stat.value}</h2>
              </div>
              <span className={`trend-indicator text-${stat.accent}`}>{stat.change}</span>
            </div>
            <div className="progress rounded-pill shadow-sm" role="progressbar">
              <div
                className={`progress-bar bg-${stat.accent}`}
                style={{ width: `${stat.progress || 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="card-soft">
      <ul className="nav nav-tabs border-0 gap-2 mb-0 px-1 pt-1" role="tablist">
        {DASHBOARD_TABS.map((tab) => (
          <li key={tab.key} className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`nav-link rounded-4 shadow-sm border-0 px-4 ${activeTab === tab.key ? 'active fw-semibold' : 'text-muted'}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="tab-content p-3 pt-4 border-top border-light mt-2">
        {activeTab === 'users' && (
          <UserManagement
            users={users}
            loading={usersLoading}
            pagination={usersPagination}
            statusFilter={usersStatusFilter}
            sortFilter={usersSort}
            onStatusChange={onUsersStatusChange}
            onPageChange={onUsersPageChange}
            onSearchChange={onUsersEmailSearch}
            onDateChange={onUsersDateSearch}
            onSortChange={onUsersSortChange}
            onRefreshUsers={onRefreshUsers}
          />
        )}
        {activeTab === 'organizations' && <OrganizationManagement />}
        {activeTab === 'subscriptions' && <SubscriptionsAndTransactions />}
      </div>
    </div>
  </div>
)};

export default Dashboard;
